import * as React from "react"
import {useEffect} from "react"
import {Grid} from "@mui/material"
import {translateCountryName} from "@ui-seeker/utils"

import {LabelCountry} from "@components"
import {GROUPED_BY, STOCK_ROUTES, USER_ROLES} from "@constants"
import {useUserApp} from "@hooks";
import {isSystemBusy} from "@signals"
import PageWrapper from "Pages/@PageWrapper"
import { TourProvider, TourTarget } from "@tour"
import {useStockAssDetailsSummary} from "./@hooks"
import {resetStockAssDetails, stockAssDetailsIsEmpty} from "./@signals"
import StockAssortmentDetailsFilters from "./Filters"
import StockAssortmentFooterInfo from "./FooterInfo"
import StockAssortmentNotListedTable from "./NotListedTable"
import StockAssortmentDetailsStockEvolutionChart from "./StockEvolutionChart"
import StockAssortmentDetailsSummary from "./Summary"
import StockAssortmentDetailsTable from "./Table"
import StockAssortmentDetailsWidgetsEvolutionChart from "./WidgetEvolution"
import StockAssDetailsReportFormDrawer from "./ReportForm";


// The three widget rows of the page. They are asked for in a single request: everything
// expensive behind them is the same and only the grouping differs.
const SUMMARY_SPLITS = [GROUPED_BY.COUNTRY, GROUPED_BY.RETAILER, GROUPED_BY.BRAND]


export default function StockAssortmentDetailsPage() {
    const { userRole } = useUserApp()
    const { data: summary, isLoading: isLoadingSummary } = useStockAssDetailsSummary(SUMMARY_SPLITS)

    useEffect(() => {
        return () => resetStockAssDetails()
    },[])

    return <PageWrapper
        isEmpty={stockAssDetailsIsEmpty.value}
        actions={null}
        tourPageKey="assDetails"
        head={`DIP Insight | ${STOCK_ROUTES.STOCK_ASSORT_TITLE}`}
        title={userRole === USER_ROLES.RETAILER ? STOCK_ROUTES.STOCK_AFFINITY_TITLE : STOCK_ROUTES.STOCK_AFFINITY_TITLE}
        subtitle={STOCK_ROUTES.STOCK_ASSORT_SUBTITLE}
        filters={<StockAssortmentDetailsFilters />}
        hasErrors={isSystemBusy.value}
    >
        <Grid container spacing={4} sx={{ mt: 1 }}>
            <Grid item xs={12} data-tour="assdetails-market-summary">
                <StockAssortmentDetailsSummary
                    rows={summary[GROUPED_BY.COUNTRY]}
                    isLoading={isLoadingSummary}
                    splitKpi={[GROUPED_BY.COUNTRY]}
                    showSkuInfo={false}
                    title="Market:"
                    zeroTitle="Markets:"
                    labelFormat={label => <LabelCountry
                        country={label}
                        label={translateCountryName(label)}
                    />}
                />
            </Grid>
            <Grid item xs={12} data-tour="assdetails-retailer-summary">
                <StockAssortmentDetailsSummary
                    rows={summary[GROUPED_BY.RETAILER]}
                    isLoading={isLoadingSummary}
                    splitKpi={[GROUPED_BY.RETAILER]}
                    showSkuInfo={true}
                    title="Retailer:"
                    zeroTitle="Retailers:"
                    labelFormat={(label, { country }) => <LabelCountry
                        country={country}
                        label={label}
                    />}
                />
            </Grid>
            <Grid item xs={12} data-tour="assdetails-brand-summary">
                <StockAssortmentDetailsSummary
                    rows={summary[GROUPED_BY.BRAND]}
                    isLoading={isLoadingSummary}
                    splitKpi={[GROUPED_BY.BRAND]}
                    showSkuInfo={true}
                    title="Brand:"
                />
            </Grid>
            <Grid item xs={12} sx={{ position: "relative" }}>
                <TourTarget name="assdetails-table" height={450} />
                <StockAssortmentDetailsTable />
            </Grid>
            <Grid item xs={12} data-tour="assdetails-not-listed">
                <StockAssortmentNotListedTable />
            </Grid>
            <Grid item xs={12}>
                <StockAssortmentDetailsStockEvolutionChart />
            </Grid>
            <Grid item xs={12}>
                <StockAssortmentDetailsWidgetsEvolutionChart />
            </Grid>
            <Grid item xs={12} data-tour="assdetails-footer">
                <StockAssortmentFooterInfo />
            </Grid>
            <Grid item xs={12}>
                <StockAssDetailsReportFormDrawer />
            </Grid>
        </Grid>
    </PageWrapper>
}
